import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play, Pause } from "lucide-react";

/**
 * ResearchLayout - Replicates the OpenAI Research page aesthetic
 * 
 * Props:
 * @param {string} title - Main title
 * @param {string} date - Publication date
 * @param {string} author - Author name(s)
 * @param {string} summary - Brief summary/preprint abstract style
 * @param {string} category - "Research", "Publication", etc.
 * @param {string} heroImage - URL for hero image
 * @param {string} heroImageCaption - Caption for hero image
 * @param {React.ReactNode} children - Main content
 * @param {Array} relatedLinks - Array of { title, date, category, path, image }
 */
const ResearchLayout = ({
  title,
  date,
  author,
  summary,
  category = "Research",
  heroImage,
  heroImageCaption,
  children,
  relatedLinks = []
}) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // To track reading time (static/estimated)
  const [readTime, setReadTime] = useState("4:12");
  
  const articleRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Dynamic read time estimation
    if (articleRef.current) {
        const text = articleRef.current.innerText + " " + title + " " + summary;
        const words = text.split(/\s+/).length;
        const minutes = Math.ceil(words / 200); // 200 wpm
        setReadTime(`${minutes}:00`);
    }

    return () => {
        // Cleanup speech on unmount
        window.speechSynthesis.cancel();
    };
  }, [title, summary]);

  const toggleSpeech = () => {
    if ('speechSynthesis' in window) {
      if (isSpeaking && !isPaused) {
        window.speechSynthesis.pause();
        setIsPaused(true);
      } else if (isSpeaking && isPaused) {
        window.speechSynthesis.resume();
        setIsPaused(false);
      } else {
        // Start speaking
        const content = `${title}. ${summary}. ${articleRef.current ? articleRef.current.innerText : ''}`;
        const utterance = new SpeechSynthesisUtterance(content);
        
        // Optional: Select a better voice
        const voices = window.speechSynthesis.getVoices();
        // Priority list for natural-sounding female voices
        const preferredVoiceNames = [
          "Google US English", // Chrome (often high quality)
          "Samantha",          // macOS
          "Microsoft Zira",    // Windows
          "Google UK English Female",
          "Karen"
        ];
        
        let selectedVoice = voices.find(v => preferredVoiceNames.some(name => v.name.includes(name))) || 
                            voices.find(v => v.name.toLowerCase().includes("female")) ||
                            voices[0];

        if (selectedVoice) utterance.voice = selectedVoice;
        
        utterance.rate = 0.9; // Just slightly slower for a more relaxing pace
        utterance.pitch = 1.05; // Slightly higher pitch for a "sweeter" tone

        utterance.onend = () => {
          setIsSpeaking(false);
          setIsPaused(false);
        };

        window.speechSynthesis.speak(utterance);
        setIsSpeaking(true);
        setIsPaused(false);
      }
    } else {
      alert("Text-to-speech is not supported in this browser.");
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = title;
    
    // Copy URL to clipboard
    navigator.clipboard.writeText(url).then(() => {
        // Redirect to share page
        let shareUrl = '';
        if (platform === 'twitter') {
            shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        } else if (platform === 'linkedin') {
            shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        }
        
        if (shareUrl) {
            window.open(shareUrl, '_blank', 'noopener,noreferrer');
        }
    }).catch(err => {
        console.error('Failed to copy: ', err);
        // Fallback if clipboard fails, still try to open window
        let shareUrl = '';
        if (platform === 'twitter') {
            shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        } else if (platform === 'linkedin') {
            shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        }
        if (shareUrl) window.open(shareUrl, '_blank', 'noopener,noreferrer');
    });
  };

  return (
    <div className="min-h-screen bg-black text-[#F4F4F4] font-sans selection:bg-[#065DC0] selection:text-white pb-32">
      {/* Top Meta Bar */}
      <div className="pt-32 pb-6 border-b border-white/10 max-w-[1200px] mx-auto px-6 flex justify-between items-end">
        <div className="flex flex-col gap-2">
           <span className="text-xs font-semibold uppercase tracking-wider text-white">{category}</span>
           <span className="text-sm text-gray-400">{date}</span>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 mt-12">
        {/* Left Sidebar / Meta */}
        <div className="hidden lg:flex lg:col-span-3 flex-col gap-8 sticky top-32 h-fit">
           <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Author</h4>
              <p className="text-sm font-medium text-white">{author}</p>
           </div>
           
           <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Share</h4>
              <div className="flex gap-4">
                 <button onClick={() => handleShare('twitter')} className="text-sm hover:underline hover:text-blue-400 transition-colors">X</button>
                 <button onClick={() => handleShare('linkedin')} className="text-sm hover:underline hover:text-blue-400 transition-colors">LinkedIn</button>
              </div>
           </div>
        </div>

        {/* Main Content Column */}
        <div className="col-span-1 lg:col-span-8 lg:col-start-4">
           {/* Header Area */}
           <div className="mb-12">
              <h1 className="text-4xl md:text-6xl font-medium tracking-tight leading-[1.1] mb-8 text-white">
                {title}
              </h1>
              
              <div className="text-lg md:text-xl leading-relaxed text-[#B4B4B4] mb-8 font-light">
                 {summary}
              </div>

               {/* Functional "Listen" Player */}
               <button 
                  onClick={toggleSpeech}
                  className={`flex items-center gap-3 bg-white/5 w-fit px-4 py-2 rounded-full border border-white/10 hover:border-white/20 transition-all mb-10 group ${isSpeaking && !isPaused ? 'border-green-500/50 bg-green-500/10' : ''}`}
               >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isSpeaking && !isPaused ? 'bg-green-500 text-black' : 'bg-white text-black'}`}>
                     {isSpeaking && !isPaused ? (
                        <Pause size={10} fill="currentColor" />
                     ) : (
                        <Play size={10} fill="currentColor" className="ml-0.5" />
                     )}
                  </div>
                  <span className="text-xs font-medium tracking-wide">
                     {isSpeaking && !isPaused ? "Pause article" : (isSpeaking ? "Resume article" : "Listen to article")}
                  </span>
                  <span className="text-xs text-gray-500 font-mono pl-2">{readTime}</span>
               </button>
           </div>

           {/* Hero Image */}
           {heroImage && (
             <figure className="mb-16">
               <img 
                 src={heroImage} 
                 alt={title} 
                 className="w-full h-auto rounded-sm border border-white/10"
               />
               {heroImageCaption && (
                 <figcaption className="mt-3 text-xs text-gray-500 font-mono">
                   {heroImageCaption}
                 </figcaption>
               )}
             </figure>
           )}

           {/* Article Body */}
           <article 
              ref={articleRef}
              className="prose prose-invert prose-lg max-w-none prose-headings:font-medium prose-headings:tracking-tight prose-p:text-[#B4B4B4] prose-p:font-light prose-p:leading-relaxed prose-p:text-justify prose-p:mb-8 prose-strong:text-white prose-strong:font-semibold prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline prose-li:text-[#B4B4B4] prose-blockquote:border-l-blue-500 prose-blockquote:bg-white/5 prose-blockquote:py-2 prose-blockquote:pr-4"
            >
              {children}
           </article>

           {/* Bottom Meta (Mobile) */}
           <div className="lg:hidden mt-16 pt-8 border-t border-white/10">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Author</h4>
              <p className="text-sm font-medium text-white mb-6">{author}</p>
           </div>
        </div>
      </div>

      {/* Keep Reading / Footer Section */}
      {relatedLinks.length > 0 && (
         <div className="max-w-[1200px] mx-auto px-6 mt-32 pt-12 border-t border-white/10">
            <h3 className="text-2xl font-medium mb-8 text-white">Keep reading</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
               {relatedLinks.map((link, idx) => (
                  <Link to={link.path} key={idx} className="group block h-full">
                     <div className="aspect-[16/9] w-full bg-black rounded-[32px] overflow-hidden mb-4 border border-white/5 relative shadow-2xl transition-all duration-700 group-hover:border-blue-500/20">
                        {link.image ? (
                           <img src={link.image} alt={link.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-60 group-hover:opacity-80" />
                        ) : (
                           <div className="w-full h-full flex items-center justify-center bg-black text-zinc-800">
                             <div className="w-12 h-12 rounded-full border border-current flex items-center justify-center font-serif italic text-2xl group-hover:text-blue-500 transition-colors">
                                F
                             </div>
                           </div>
                        )}
                        {/* Blue Ambient Glow on Hover */}
                        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-blue-600/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                        <div className="absolute top-2 left-2 bg-black/80 backdrop-blur px-3 py-1 rounded-full text-[10px] text-zinc-400 uppercase font-bold tracking-widest border border-white/5">
                           {link.category}
                        </div>
                     </div>
                     <h4 className="text-xl font-medium text-white group-hover:text-blue-400 transition-colors leading-snug mb-2 tracking-tight">
                        {link.title}
                     </h4>
                     <div className="flex items-center gap-2 text-xs text-zinc-600 uppercase tracking-widest font-bold">
                        {link.date}
                        {link.external && <ArrowUpRight size={12} />}
                     </div>
                  </Link>
               ))}
            </div>
         </div>
      )}
    </div>
  );
}

export default ResearchLayout;
